<template>
  <div class="signup-form bg-white p-8 rounded-lg shadow-md max-w-md w-full">
    <h2 class="text-2xl font-bold text-center mb-6">Create Your Account</h2>
    
    <div v-if="authStore.error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      {{ authStore.error }}
    </div>
    
    <div v-if="formSubmitted && !authStore.error" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
      Account created successfully! Logging you in...
    </div>
    
    <form @submit.prevent="handleSubmit">
      <div class="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label for="firstName" class="block text-gray-700 mb-2">First Name</label>
          <input 
            type="text" 
            id="firstName"
            v-model="form.firstName"
            class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="First name"
            required
          >
        </div>
        <div>
          <label for="lastName" class="block text-gray-700 mb-2">Last Name</label>
          <input 
            type="text" 
            id="lastName"
            v-model="form.lastName"
            class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Last name"
            required
          >
        </div>
      </div>
      
      <div class="mb-4">
        <label for="email" class="block text-gray-700 mb-2">Email</label>
        <input 
          type="email" 
          id="email"
          v-model="form.email"
          class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Your email address"
          required
        >
      </div>
      
      <div class="mb-4">
        <label for="password" class="block text-gray-700 mb-2">Password</label>
        <input 
          type="password" 
          id="password"
          v-model="form.password"
          class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Create a password"
          required
        >
        <p class="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
      </div>
      
      <div class="mb-6">
        <label for="confirmPassword" class="block text-gray-700 mb-2">Confirm Password</label>
        <input 
          type="password" 
          id="confirmPassword"
          v-model="form.confirmPassword"
          class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Confirm your password"
          required
        >
        <p v-if="passwordsDoNotMatch" class="text-xs text-red-500 mt-1">Passwords do not match</p>
      </div>
      
      <div class="mb-6">
        <label for="role" class="block text-gray-700 mb-2">Role</label>
        <select 
          id="role"
          v-model="form.role"
          class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          required
        >
          <option value="user">User</option>
          <option value="agent">Agent</option>
        </select>
      </div>
      
      <div class="mb-6 flex items-center">
        <input 
          type="checkbox" 
          id="terms" 
          v-model="form.agreeToTerms"
          class="h-4 w-4 text-primary-600 focus:ring-primary-500"
          required
        >
        <label for="terms" class="ml-2 block text-gray-700">
          I agree to the 
          <a href="#" class="text-primary-600 hover:text-primary-800">Terms of Service</a> 
          and 
          <a href="#" class="text-primary-600 hover:text-primary-800">Privacy Policy</a>
        </label>
      </div>
      
      <button 
        type="submit" 
        class="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        :disabled="authStore.loading || !isFormValid"
      >
        <span v-if="authStore.loading">
          <i class="fas fa-spinner fa-spin mr-2"></i>Creating account...
        </span>
        <span v-else>Create Account</span>
      </button>
    </form>
    
    <div class="mt-6 text-center">
      <p class="text-gray-600">
        Already have an account? 
        <router-link to="/login" class="text-primary-600 hover:text-primary-800">
          Login
        </router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const router = useRouter();
const formSubmitted = ref(false);

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'user',
  agreeToTerms: false
});

const passwordsDoNotMatch = computed(() => {
  return form.password && form.confirmPassword && form.password !== form.confirmPassword;
});

const isPasswordValid = computed(() => {
  // Less strict password validation for testing
  return form.password && form.password.length >= 8;
});

const isFormValid = computed(() => {
  return form.firstName && 
         form.lastName && 
         form.email && 
         isPasswordValid.value && 
         !passwordsDoNotMatch.value && 
         form.agreeToTerms;
});

const handleSubmit = async () => {
  if (!isFormValid.value) {
    console.log('Form is not valid, validation issues:', {
      firstName: !!form.firstName,
      lastName: !!form.lastName,
      email: !!form.email,
      passwordValid: isPasswordValid.value,
      passwordsMatch: !passwordsDoNotMatch.value,
      agreeToTerms: form.agreeToTerms
    });
    return;
  }
  
  try {
    console.log('Submitting signup form with data:', {
      email: form.email,
      first_name: form.firstName,
      last_name: form.lastName,
      role: form.role,
      password: 'HIDDEN'
    });
    
    // Set form as submitted
    formSubmitted.value = true;
    
    await authStore.signup({
      email: form.email,
      password: form.password,
      first_name: form.firstName,
      last_name: form.lastName,
      role: form.role
    });
    
    console.log('Signup successful, store state:', {
      user: authStore.user,
      isAuthenticated: authStore.isAuthenticated,
      error: authStore.error
    });
    
    // If signup was successful (no errors), redirect to dashboard after a delay
    if (!authStore.error) {
      setTimeout(() => {
        router.push('/dashboard');
      }, 1500);
    }
  } catch (error) {
    console.error('Signup submission error:', error);
    // Reset form submitted state if there was an error
    formSubmitted.value = false;
  }
};
</script>
